import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps, useWindowDimensions } from 'react-native'
import { ApiError, DataWithUpload, Draft, FloorplanData, useFloorplanMutation, useStore } from 'zens-store'
import { Suspense, useState } from 'react'
import { useRouter, useNumberParams } from 'zens-router'
import { FloorplanEditor, FloorplanEditorStep, FloorplanHeader, LayoutHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
})

interface Props extends ViewProps {}

const FloorAddScreen = (props: Props) => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const store = useStore()
  const { message } = store
  const { t } = useTranslation()
  const { style, ...otherProps } = props
  const { height } = useWindowDimensions()
  const contentStyle = { minHeight: height }
  const [{ companyId, locationId, buildingId }] = useNumberParams()
  const mutation = useFloorplanMutation({ companyId })

  async function save(draft: Draft<DataWithUpload<FloorplanData>>) {
    setErrors({})
    const { upload, ...otherData } = draft.data
    try {
      delete otherData.floorplan?.background
      const floor = await mutation.create(new Draft(otherData))
      draft.update('id', floor.id)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`FLOORPLAN: Floorplan upload failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Floor could not be added')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }
    draft.settle()
    message.add({
      message: t('Floor was added successfully'),
      status: 'success',
    })
    if (upload) {
      try {
        const newFloorplan = await mutation.upload(draft, upload)
        draft.update('floorplan', { ...draft.data.floorplan, background: newFloorplan.background })
      } catch (error) {
        const { message: errorMessage } = error as Error
        console.debug(`FLOORPLAN: Floor upload failed: ${errorMessage}`)
        message.add({
          message: `${t('Floorplan background could not be uploaded')}. ${errorMessage}`,
          status: 'error',
        })
        draft.settle()
        router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
        return
      }
    }
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${draft.data.id}/edit`)
  }

  async function cancel() {
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
  }

  return (
    <View style={[styles.container, contentStyle, style]} {...otherProps}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <FloorplanHeader
          companyId={companyId}
          locationId={locationId}
          buildingId={buildingId}
          breadcrumbItems={[{ title: t('Add floor') }]}
          step={FloorplanEditorStep.Add}
        />
      </Suspense>
      <Suspense fallback={<FloorplanEditor.Skeleton style={styles.content} step={FloorplanEditorStep.Add} />}>
        <FloorplanEditor
          style={styles.content}
          companyId={companyId}
          buildingId={buildingId}
          step={FloorplanEditorStep.Add}
          errors={errors}
          onSaveDraft={save}
          onCancel={cancel}
        />
      </Suspense>
    </View>
  )
}

export default FloorAddScreen
